/* eslint-disable import-path/forbidden */

import _ from 'lodash';
import React from 'react';
import classnames from 'classnames';
import ReactTooltip from 'react-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Knob from 'design-system/Knob';
import { Tooltip } from 'design-system';
import GrowingTextArea from './GrowingTextArea';
import Select from 'design-system/SelectInput';
import { IS_FEDERAL } from '@scale/frontend-lib/constants';
import ColorPalette from 'react-scale/components/icons/ColorPalette';
import LinkedAnnotation from 'react-scale/components/scalers/tasks/common/LinkedAnnotation';
import { AnnotationAttributeType } from '@scale/shared/defaults';
import { sanitizeString } from '@scale/shared/grader/descriptions/helpers';
import {
  getAnnotationId,
  getAllChoiceDescendants,
  getDisplayLabel,
  BreakdownColors,
} from '@scale/shared/annotation';
import { IInstructionListJson } from '@scale/shared/types/instructionitem';
import {
  AnnotationAttribute,
  AnnotationAttributes,
  Annotation,
  LinkNotPresent,
  AttributeSelections,
} from '@scale/shared/types/annotation';
import { AttributeFlag } from '@scale/shared/types/ontology';
import { convertNumberInput } from './utils';
import XYLineInput from './XYLineInput';
import AttributeRadioTree from './AttributeRadioTree';
import AttributeShortcutSelectWrapper from './AttributeShortcutSelectWrapper';
import { AttributeSelectTree } from './AttributeSelectTree';
import { AttributeSearchSelectTree } from './AttributeSearchSelectTree';
import { AttributeSelectSequence } from './AttributeSelectSequence';
import { AttributeCheckboxMultiSelect } from './AttributeCheckboxMultiSelect';
import { AttributeMultiSelect } from './AttributeMultiSelect';
import AttributeNumberInput from './AttributeNumberInput';
import AudioAttributeInput from './AudioAttributeInput';
import AttributeNumberArrayInput from './AttributeNumberArrayInput';
import { LabelChoice, getLabelChoiceValue } from '@scale/shared/types/params/annotation';
import AttributeShortcutMultiSelect from 'react-scale/components/scalers/common/AnnotationAttributeInput/AttributeShortcutMultiSelect';
import { AssignmentJson } from '@scale/shared/assignmentJson/types/AssignmentJson';
import { AttributePrelabelWrapper } from './AttributePrelabelWrapper';
import CategoryFieldSectionWrapper from 'react-scale/components/scalers/common/AnnotationAttributeInput/CategoryFieldSectionWrapper';

// arbitrary # of items that seems long
const SWITCH_TO_DROP_DOWN_EXPERIMENT_THRESHOLD = 8;
const SEARCH_SELECT_THRESHOLD = 8;

function convertTextInput(text: string | string[] | number | undefined): string | undefined {
  if (!_.isString(text)) {
    return;
  }
  // Filter out empty strings
  if (!text.length) {
    return;
  }
  if (Array.isArray(text)) {
    return text.join(' ');
  }
  return text;
}

type AnnotationAttributeInputState = {
  startFrame?: number | undefined;
  endFrame?: number | undefined;
  value?: string | undefined;
};

interface AnnotationAttributeInputProps {
  assignment?: AssignmentJson;
  attrKey: string;
  attrParams: $FIXME;
  description?: string;
  handleUpdateSelection: (attrKey: string, attrVal: $FIXME) => void;
  flags?: {
    useSelectForLargeChoiceCount: boolean;
    useSequentialForLargeLabelCount?: boolean;
  };
  annotations?: Annotation[];
  annotationId?: string;
  type?: 'compact';
  isReadOnly?: boolean;
  className?: string;
  showReadOnlyAsLocked?: boolean;
  colorMapping?: Record<string, string>;
  selectedChoice?: string | number | string[];
  instructionList?: IInstructionListJson;
  setColorMapping?: (attrKey: string) => void;
  label?: string;
  attrDefinitions?: AnnotationAttributes;
  selectedBreakdown?: string | null;
  breakdownColors?: BreakdownColors;
  handleLinkedAttributeClick?: (annotation: Annotation) => void;
  dotKeyValueTable?: Record<string, Record<string, string[]>>;
  allowedNumbers?: number[];
  useUnified?: boolean;
  shortcutDisplay?: boolean;
  isPrelabel?: boolean;
  markPrelabel?: () => void;
  numFrames?: number;
  attributeFlag?: AttributeFlag;
}

class AnnotationAttributeInput extends React.Component<AnnotationAttributeInputProps> {
  static defaultFlags = {
    useSelectForLargeChoiceCount: true,
    useSequentialForLargeLabelCount: false,
  };
  state: AnnotationAttributeInputState = {};

  get annotation() {
    return _.find(this.props.annotations, a => getAnnotationId(a) === this.props.annotationId);
  }

  get isCompact() {
    return this.props.type === 'compact';
  }

  get isCategoryFieldSectionWrapper() {
    return (
      this.props.assignment?.featureFlags?.skipAnnotationAttributeChoiceValidation &&
      this.props.assignment?.featureFlags?.categoryFieldSectionWrapper &&
      !this.props.attrParams.conditions?.is_global
    );
  }

  get isSearchSelect() {
    return (
      this.props.assignment?.featureFlags?.attributeSearchSelect &&
      this.props.attrParams.choices.length >= SEARCH_SELECT_THRESHOLD
    );
  }

  get isUnified() {
    return (
      (this.props.attrParams.type === undefined ||
        this.props.attrParams.type === 'label' ||
        this.props.attrParams.type === AnnotationAttributeType.Category) &&
      (this.isCompact || this.shouldPreferSelectOverRadioInputs) &&
      !(
        (IS_FEDERAL || this.props.flags?.useSequentialForLargeLabelCount) &&
        this.props.attrKey === 'label'
      ) &&
      !(this.props.isReadOnly && this.props.showReadOnlyAsLocked && this.props.selectedChoice) &&
      !this.props.attrParams.allow_multiple
    );
  }

  get shouldPreferSelectOverRadioInputs() {
    const { attrParams, flags } = this.props;
    const hasHint = attrParams.choices.some(
      (choice: LabelChoice) => !_.isString(choice) && choice.hint,
    );
    return (
      _.defaults({}, flags, AnnotationAttributeInput.defaultFlags).useSelectForLargeChoiceCount &&
      getAllChoiceDescendants(attrParams.choices).length >=
        SWITCH_TO_DROP_DOWN_EXPERIMENT_THRESHOLD &&
      !hasHint
    );
  }

  handleUpdateSelection = (attrKey: string, attrVal: string | string[] | number | undefined) => {
    this.props.handleUpdateSelection(attrKey, attrVal);
  };

  /* ---------- Input: Radio ------------------------------------------------ */
  handleRadioSelect = (choice: LabelChoice) => {
    const attribute: AnnotationAttribute | undefined = _.get(
      this.props.attrDefinitions,
      this.props.attrKey,
    );
    if (
      attribute &&
      attribute.optional &&
      getLabelChoiceValue(choice) === this.props.selectedChoice
    ) {
      this.handleUpdateSelection(this.props.attrKey, undefined);
    } else {
      this.handleUpdateSelection(this.props.attrKey, getLabelChoiceValue(choice));
    }
  };

  renderRadioInput() {
    if (this.props.shortcutDisplay) {
      return (
        <AttributeShortcutSelectWrapper
          attrKey={this.props.attrKey}
          attrParams={this.props.attrParams}
          selectedChoice={convertTextInput(this.props.selectedChoice)}
          handleSelect={this.handleRadioSelect}
          isReadOnly={this.props.isReadOnly}
        />
      );
    }

    return (
      <div key={this.props.attrKey}>
        {this.props.attrParams.choices &&
          this.props.attrParams.choices.map((c: $FIXME) => {
            return (
              <AttributeRadioTree
                key={c.choice || c}
                tree={c}
                selectedChoice={convertTextInput(this.props.selectedChoice)}
                isReadOnly={this.props.isReadOnly}
                handleSelect={this.handleRadioSelect}
                colorMapping={this.props.colorMapping}
                instructionList={this.props.instructionList}
              />
            );
          })}
      </div>
    );
  }

  renderSelectInput(description?: string) {
    const convertedTextChoice = convertTextInput(this.props.selectedChoice);
    const useUnified = description !== undefined;

    // TODO: Implement search select for prelabels
    const useSearchSelect = this.isSearchSelect && !this.props.isPrelabel;
    const attributeSelectTreeWithTooltip = (
      <Tooltip title={this.props.isReadOnly ? convertedTextChoice : ''}>
        <div
          className="attribute-select-tree-container"
          data-tip={useUnified ? true : null}
          data-for={useUnified ? `attr-desc-${this.props.attrKey}` : null}
        >
          <AttributeSelectTree
            key={this.props.attrKey}
            isReadOnly={this.props.isReadOnly}
            selectedChoice={convertedTextChoice}
            choices={this.props.attrParams.choices}
            handleSelect={this.handleRadioSelect}
            description={description}
            isPrelabel={this.props.isPrelabel}
          />
          {useUnified && this.renderAttributeDescTooltip(description)}
          <style jsx>{`
            .attribute-select-tree-container {
              flex-grow: 1;
              display: flex;
              flex-flow: row wrap;
            }
          `}</style>
        </div>
      </Tooltip>
    );

    const nonSearchSelect = this.props.isPrelabel ? (
      <AttributePrelabelWrapper markPrelabel={this.props.markPrelabel}>
        {attributeSelectTreeWithTooltip}
      </AttributePrelabelWrapper>
    ) : (
      attributeSelectTreeWithTooltip
    );

    return useSearchSelect ? (
      <AttributeSearchSelectTree
        key={this.props.attrKey}
        isReadOnly={this.props.isReadOnly}
        selectedChoice={convertedTextChoice}
        choices={this.props.attrParams.choices}
        handleSelect={this.handleRadioSelect}
        description={description}
      />
    ) : (
      nonSearchSelect
    );
  }

  renderSelectSequenceInput() {
    return (
      <div className="attribute-select-tree-container">
        <AttributeSelectSequence
          key={this.props.attrKey}
          isReadOnly={this.props.isReadOnly}
          selectedChoice={convertTextInput(this.props.selectedChoice)}
          choices={this.props.attrParams.choices}
          handleSelect={this.handleRadioSelect}
        />
        <style jsx>{`
          .attribute-select-tree-container {
            flex-grow: 1;
            display: flex;
            flex-flow: row wrap;
          }
        `}</style>
      </div>
    );
  }

  renderAttributeLocked() {
    const { selectedChoice } = this.props;

    return (
      <div className="attribute-locked">
        <FontAwesomeIcon icon="lock" />
        <span style={{ padding: '0 4px' }} />
        {this.props.attrKey === 'label' && _.isString(selectedChoice)
          ? getDisplayLabel(selectedChoice, this.props.attrParams.choices)
          : selectedChoice}

        <style jsx>{`
          .attribute-locked {
            background: var(--color-RemoGray01);
            border-radius: var(--border-radius);
            padding: 4px 8px;
          }
        `}</style>
      </div>
    );
  }

  /* ---------- Input: Multi-Select -------------------------------------------- */
  handleMultiSelect = (attrVal: string[]) => {
    this.handleUpdateSelection(this.props.attrKey, attrVal);
  };

  renderSearchMultiSelectInput() {
    const { selectedChoice } = this.props;
    return (
      <AttributeSearchSelectTree
        key={this.props.attrKey}
        isReadOnly={this.props.isReadOnly}
        selectedChoices={
          Array.isArray(selectedChoice) || _.isNil(selectedChoice)
            ? selectedChoice
            : ([selectedChoice] as string[] | undefined)
        }
        choices={this.props.attrParams.choices}
        handleMultiSelect={this.handleMultiSelect}
        handleSelect={() => {}}
        isMulti={true}
      />
    );
  }

  renderCategoryFieldSectionWrapper(isSingleSelect: boolean = false) {
    const { selectedChoice } = this.props;
    return (
      <CategoryFieldSectionWrapper
        attrKey={this.props.attrKey}
        id={this.props.annotationId}
        choices={this.props.attrParams.choices}
        handleMultiSelect={this.handleMultiSelect}
        selectedChoices={
          Array.isArray(selectedChoice) || _.isNil(selectedChoice)
            ? selectedChoice
            : ([selectedChoice] as string[] | undefined)
        }
        isSingleSelect={isSingleSelect}
        handleUpdateSelection={this.handleUpdateSelection}
      />
    );
  }

  renderCheckboxMultiSelectInput() {
    const { selectedChoice } = this.props;
    return (
      <AttributeCheckboxMultiSelect
        key={this.props.attrKey}
        isReadOnly={this.props.isReadOnly}
        // HACK: remove this after fixing CH151565
        selectedChoices={
          Array.isArray(selectedChoice) || _.isNil(selectedChoice)
            ? selectedChoice
            : ([selectedChoice] as string[] | undefined)
        }
        choices={this.props.attrParams.choices}
        handleSelect={this.handleMultiSelect}
      />
    );
  }

  renderCheckboxShortcutsMultiSelectInput() {
    const { selectedChoice } = this.props;
    return (
      <AttributeShortcutMultiSelect
        attrKey={this.props.attrKey}
        isReadOnly={this.props.isReadOnly}
        selectedChoices={
          Array.isArray(selectedChoice) || _.isNil(selectedChoice)
            ? selectedChoice
            : ([selectedChoice] as string[] | undefined)
        }
        attrParams={this.props.attrParams}
        handleSelect={this.handleMultiSelect}
      />
    );
  }

  renderMultiSelectInput() {
    const { selectedChoice } = this.props;
    return (
      <div className="attribute-multi-select-container">
        <AttributeMultiSelect
          key={this.props.attrKey}
          isReadOnly={this.props.isReadOnly}
          // HACK: remove this after fixing CH151565
          selectedChoice={
            Array.isArray(selectedChoice) || _.isNil(selectedChoice)
              ? selectedChoice
              : ([selectedChoice] as string[] | undefined)
          }
          choices={this.props.attrParams.choices}
          handleSelect={this.handleMultiSelect}
        />
        <style jsx>{`
          .attribute-multi-select-container {
            flex-grow: 1;
            display: flex;
            flex-flow: row wrap;
          }
        `}</style>
      </div>
    );
  }

  /* ---------- Input: Angle ----------------------------------------------- */

  handleAngleInputChange = (newValue: number) => {
    if (!this.props.isReadOnly)
      return this.handleUpdateSelection(this.props.attrKey, convertNumberInput(newValue));
  };

  renderAngleInput = () => {
    return (
      <div className="angle-input">
        <Knob
          value={this.props.selectedChoice as number}
          min={0}
          max={360}
          width={100}
          height={100}
          thickness={0.3}
          fgColor={this.props.isReadOnly ? '#BBBBBB' : '#222222'}
          onChange={this.handleAngleInputChange}
        />

        <style jsx>{`
          .angle-input {
            margin-bottom: 12px;
          }
        `}</style>
      </div>
    );
  };

  renderDotKeyValue(): React.ReactNode {
    if (!this.props.dotKeyValueTable || !this.props.annotations || !this.props.annotationId) {
      return null;
    }
    const options: { label: string; value: string }[] = [
      { label: '', value: '' },
      { label: 'Not Present in Document', value: LinkNotPresent },
    ];
    const matches =
      this.props.dotKeyValueTable?.[this.props.attrKey]?.[this.props.annotationId] || [];
    for (const match of matches) {
      const annotation = this.props.annotations.find(annotation => annotation.uuid === match);
      if (annotation && annotation.uuid && annotation.attributes) {
        options.push({
          label: getDotKeyValueLabel(annotation.uuid, annotation.attributes),
          value: annotation.uuid,
        });
      }
    }
    const onChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
      const attrVal = event.target.value;
      this.props.handleUpdateSelection(this.props.attrKey, attrVal);
    };
    return (
      <div>
        <Select
          disabled={this.props.isReadOnly}
          value={this.props.selectedChoice}
          onChange={onChange}
          blurOnChange
          fullWidth
        >
          {options.map(data => {
            return (
              <option key={data.value} value={data.value}>
                {data.label}
              </option>
            );
          })}
        </Select>
      </div>
    );
  }

  renderLinkedButton() {
    return (
      <div className="linked-input">
        <LinkedAnnotation
          annotationId={this.props.annotationId || ''}
          annotations={this.props.annotations || []}
          attrDefinitions={this.props.attrDefinitions || {}}
          attrKey={this.props.attrKey}
          breakdownColors={this.props.breakdownColors || {}}
          handleAttributesChange={this.handleUpdateSelection}
          handleLinkedAttributeClick={this.props.handleLinkedAttributeClick || _.noop}
          isEditableMode={!this.props.isReadOnly}
          selectedBreakdown={this.props.selectedBreakdown || ''}
          selectedChoice={convertTextInput(this.props.selectedChoice) || ''}
        />
      </div>
    );
  }

  /* ---------- Input: Text ----------------------------------------------- */

  handleTextInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    return this.handleUpdateSelection(this.props.attrKey, convertTextInput(e.target.value));
  };

  renderTextInput() {
    return (
      <div className="text-input">
        <GrowingTextArea
          canUseGrammarCheck={this.props.assignment?.featureFlags?.canUseGrammarCheck}
          onChange={this.handleTextInputChange}
          onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) =>
            _.get(this.props.attrParams, 'sanitize') &&
            this.handleUpdateSelection(this.props.attrKey, sanitizeString(e.target.value))
          }
          placeholder="Your answer"
          value={this.props.selectedChoice ?? ''}
        />
        <style jsx>{`
          .text-input {
            margin-bottom: 16px;
          }
        `}</style>
      </div>
    );
  }

  /* ---------- Input: Per Frame ----------------------------------------------- */
  getNumFrames(): number {
    return this.props.numFrames ?? 1;
  }

  getPerFrameValues() {
    const { selectedChoice } = this.props;
    const numFrames = this.getNumFrames();
    if (selectedChoice && Array.isArray(selectedChoice) && selectedChoice.length === numFrames) {
      return selectedChoice as (string | undefined)[];
    }
    return new Array(numFrames).fill(undefined);
  }

  isValidStartAndEndFrames() {
    const { startFrame, endFrame } = this.state;
    const numFrames = this.getNumFrames();
    return (
      !_.isNil(numFrames) &&
      !_.isNil(startFrame) &&
      !_.isNil(endFrame) &&
      startFrame >= 0 &&
      startFrame <= endFrame &&
      endFrame < numFrames
    );
  }

  handlePerFrameAttributeChange = (value: string) => {
    if (!this.isValidStartAndEndFrames()) {
      return;
    }
    const selectedChoice = this.getPerFrameValues();
    const { startFrame, endFrame } = this.state;
    for (let i = startFrame!; i <= endFrame!; i++) {
      selectedChoice[i] = value;
    }
    this.props.handleUpdateSelection(this.props.attrKey, selectedChoice);
  };

  renderPerFrameValues() {
    const selectedChoice = this.getPerFrameValues();
    let currValue = selectedChoice[0];
    const valuesByFrame: {
      startFrame: number | undefined;
      endFrame: number | undefined;
      value: string | undefined;
    }[] = [{ value: currValue, startFrame: 0, endFrame: undefined }];

    for (let i = 0; i < selectedChoice.length; i++) {
      if (currValue !== selectedChoice[i]) {
        valuesByFrame[valuesByFrame.length - 1].endFrame = i - 1;
        currValue = selectedChoice[i];
        valuesByFrame.push({
          value: selectedChoice[i],
          startFrame: i,
          endFrame: selectedChoice.length - 1 ? i : undefined,
        });
      } else if (currValue === selectedChoice[i] && i === selectedChoice.length - 1) {
        valuesByFrame[valuesByFrame.length - 1].endFrame = i;
      }
    }

    return (
      <table className="table-auto">
        <tbody>
          {valuesByFrame.map(({ startFrame, endFrame, value }, idx) => {
            return (
              <tr key={idx}>
                <td className="pr-6">
                  Frames {startFrame as number} - {endFrame as number}
                </td>
                <td>
                  {value ?? (
                    <span className="flex items-center gap-1">
                      <FontAwesomeIcon icon="exclamation-triangle" />
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  renderPerFrameInput() {
    const { startFrame, endFrame, value } = this.state;
    const numFrames = this.getNumFrames();
    return (
      <div className="scaleui scale-tailwind scale-theme text-[#000000b3]">
        {this.renderPerFrameValues()}

        <div className="px-2 mt-2 border-1 rounded-lg border-[#000000b3]">
          <div className="flex items-center">
            <div className="pr-3" data-tip data-for={`framerange-${this.props.attrKey}`}>
              <span>Frame Range:</span>
              <ReactTooltip id={`framerange-${this.props.attrKey}`}>
                <span>
                  <span style={{ opacity: 0.5 }}>Frame Range</span>
                  <br />
                  <div style={{ maxWidth: '500px' }}>
                    Select frame range to Update {this.props.attrKey} in
                  </div>
                </span>
              </ReactTooltip>
            </div>
            <AnnotationAttributeInput
              assignment={this.props.assignment}
              isReadOnly={this.props.isReadOnly}
              attrKey={''}
              attrParams={{
                type: AnnotationAttributeType.Number,
                min: 0,
                max: numFrames - 1,
                description: 'Start frame number to update',
              }}
              selectedChoice={startFrame}
              handleUpdateSelection={(_, attrValue) => {
                this.setState({ startFrame: attrValue, value: undefined });
              }}
              handleLinkedAttributeClick={() => {}}
              annotationId={this.props.annotationId}
              annotations={this.props.annotations}
            />
            <div className="pr-3">to</div>
            <AnnotationAttributeInput
              assignment={this.props.assignment}
              isReadOnly={this.props.isReadOnly}
              attrKey={''}
              attrParams={{
                type: AnnotationAttributeType.Number,
                min: 0,
                max: numFrames - 1,
                description: 'End frame number to update',
              }}
              selectedChoice={endFrame}
              handleUpdateSelection={(_, attrValue) => {
                this.setState({ endFrame: attrValue, value: undefined });
              }}
              handleLinkedAttributeClick={() => {}}
              annotationId={this.props.annotationId}
              annotations={this.props.annotations}
            />
          </div>
          {this.isValidStartAndEndFrames() && (
            <div className="mt-[-12px] mb-2">
              <AnnotationAttributeInput
                {...this.props}
                attrParams={{
                  ...this.props.attrParams,
                  per_frame: false,
                }}
                selectedChoice={value}
                handleUpdateSelection={(_, attrValue) => {
                  this.setState({ value: attrValue });
                  this.handlePerFrameAttributeChange(attrValue);
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ---------- Base rendering ---------------------------------------------- */

  handleAttributeDescClick = () => {
    if (this.props.setColorMapping) {
      this.props.setColorMapping(this.props.attrKey);
    }
  };

  getDescription(attribute: AnnotationAttribute | undefined): string {
    const description = this.props.attrKey;
    const label = this.props.label;
    const isDotKeyValue =
      label && attribute && attribute.type === 'linked' && attribute.variant === 'dot-key-value';
    if (isDotKeyValue && _.endsWith(label, '.key')) {
      return 'Link the corresponding value';
    }
    if (isDotKeyValue && _.endsWith(label, '.value')) {
      return 'Link the corresponding key';
    }
    return description;
  }

  renderAttributeDescTooltip(description: string) {
    return (
      <ReactTooltip id={`attr-desc-${this.props.attrKey}`}>
        {this.props.attrKey === 'label' ? (
          this.props.attrParams.description
        ) : (
          <span>
            <span style={{ opacity: 0.5 }}>{description}</span>
            <br />
            <div style={{ maxWidth: '500px' }}>{this.props.attrParams.description}</div>
          </span>
        )}
      </ReactTooltip>
    );
  }

  renderAttributeDesc() {
    const attribute: AnnotationAttribute | undefined = _.get(
      this.props.attrDefinitions,
      this.props.attrKey,
    );
    const description = this.getDescription(attribute);

    let actions;
    if (this.props.setColorMapping) {
      actions = (
        <span
          onClick={this.handleAttributeDescClick}
          data-tip={
            this.props.colorMapping
              ? 'Coloring by this attribute'
              : 'Click to color by this attribute'
          }
          data-for="coloring-by"
        >
          <ColorPalette style={{ width: '24px', opacity: this.props.colorMapping ? 1 : 0.15 }} />
          <ReactTooltip id="coloring-by" />
        </span>
      );
    }
    return (
      <div
        className={`attr-desc ${this.isCompact ? 'is-compact' : ''}`}
        data-tip
        data-for={`attr-desc-${this.props.attrKey}`}
        onClick={() => {
          if (this.props.attributeFlag?.confirmed === false) {
            this.handleUpdateSelection(this.props.attrKey, this.props.selectedChoice); // Confirms flag if unconfirmed
          }
        }}
      >
        <span>{description}</span>
        <span style={{ paddingLeft: 5, opacity: 0.5 }}>
          {attribute && attribute.optional ? '(optional)' : ''}
        </span>
        <span className="actions">{actions}</span>
        {this.renderAttributeDescTooltip(description)}
        <style jsx>{`
          .attr-desc {
            margin-bottom: 4px;

            display: flex;
            align-items: flex-start;

            overflow: hidden;
          }

          .attr-desc .actions {
            margin-left: 4px;
            flex-shrink: 0;
            cursor: pointer;
          }

          .attr-desc.is-compact {
            margin-bottom: 0;
          }
        `}</style>
      </div>
    );
  }

  // eslint-disable-next-line complexity
  renderInput(): React.ReactNode {
    const attribute: AnnotationAttribute | undefined = _.get(
      this.props.attrDefinitions,
      this.props.attrKey,
    );
    const description = this.getDescription(attribute);

    if (attribute && attribute.type === 'linked' && attribute.variant === 'dot-key-value') {
      return this.renderDotKeyValue();
    }
    if (this.props.attrParams.per_frame) {
      return this.renderPerFrameInput();
    }

    let input;
    switch (this.props.attrParams.type) {
      case AnnotationAttributeType.Number:
        input = (
          <AttributeNumberInput
            attrParams={this.props.attrParams}
            attrKey={this.props.attrKey}
            selectedChoice={this.props.selectedChoice as string | undefined}
            handleUpdateSelection={this.handleUpdateSelection}
            shortcutDisplay={this.props.shortcutDisplay}
            isReadOnly={this.props.isReadOnly}
          />
        );
        break;
      case AnnotationAttributeType.Angle:
        input = this.renderAngleInput();
        break;
      case AnnotationAttributeType.XLine:
      case AnnotationAttributeType.YLine:
        input = (
          <XYLineInput
            annotation={this.annotation}
            attrType={this.props.attrParams.type}
            attrKey={this.props.attrKey}
            selectedChoice={this.props.selectedChoice}
            handleUpdateSelection={this.props.handleUpdateSelection}
          />
        );
        break;
      case AnnotationAttributeType.Linked:
        input = this.renderLinkedButton();
        break;
      case AnnotationAttributeType.Text:
      case AnnotationAttributeType.AudioTranscription:
        input = this.renderTextInput();
        break;
      case AnnotationAttributeType.Audio:
        input = (
          <AudioAttributeInput
            attrKey={this.props.attrKey}
            attrValue={this.props.selectedChoice as string | null | undefined}
            onValueChange={this.props.handleUpdateSelection}
          />
        );
        break;
      case AnnotationAttributeType.NumberArray:
        input = (
          <AttributeNumberArrayInput
            annotationId={this.props.annotationId || ''}
            attrParams={this.props.attrParams}
            attrKey={this.props.attrKey}
            attrValue={this.props.selectedChoice as string | undefined}
            handleUpdateAttrValue={this.props.handleUpdateSelection}
            allowedNumbers={this.props.allowedNumbers}
          />
        );
        break;
      case AnnotationAttributeType.Category:
      default:
        if (this.props.isReadOnly && this.props.showReadOnlyAsLocked && this.props.selectedChoice) {
          input = this.renderAttributeLocked();
        } else if (this.props.attrParams.allow_multiple) {
          if (this.isSearchSelect) {
            input = this.renderSearchMultiSelectInput();
          } else if (this.isCategoryFieldSectionWrapper) {
            input = this.renderCategoryFieldSectionWrapper();
          } else if (this.props.shortcutDisplay) {
            input = this.renderCheckboxShortcutsMultiSelectInput();
          } else if (this.isCompact) {
            input = this.renderMultiSelectInput();
          } else {
            input = this.renderCheckboxMultiSelectInput();
          }
        } else if (this.isCategoryFieldSectionWrapper) {
          input = this.renderCategoryFieldSectionWrapper(true);
        } else if (this.isCompact || this.shouldPreferSelectOverRadioInputs) {
          if (
            (IS_FEDERAL || this.props.flags?.useSequentialForLargeLabelCount) &&
            this.props.attrKey === 'label'
          ) {
            input = this.renderSelectSequenceInput(); // large label taxonomy UX experiment
          } else {
            input = this.renderSelectInput(
              this.props.useUnified && this.isUnified ? description : undefined,
            );
          }
        } else {
          input = this.renderRadioInput();
        }
    }
    return input || null;
  }

  render() {
    const unified = this.props.useUnified && this.isUnified;

    const className = classnames('attribute', this.props.className, {
      'is-readonly':
        this.props.isReadOnly && this.props.attrParams.type !== AnnotationAttributeType.Linked,
      'is-legend': !_.isEmpty(this.props.colorMapping),
      'is-compact':
        !unified && this.isCompact && this.props.attrParams.type !== AnnotationAttributeType.Linked,
      'is-flagged': !!this.props.attributeFlag,
      'flag-unconfirmed': this.props.attributeFlag?.confirmed === false,
    });

    return (
      <Tooltip
        placement="left"
        title={
          this.props.attributeFlag && (
            <ul style={{ fontSize: 12, paddingLeft: 10 }}>
              {this.props.attributeFlag.descriptions.map(description => (
                <li key={description}>{description}</li>
              ))}
            </ul>
          )
        }
      >
        <div className={className}>
          {!unified && this.renderAttributeDesc()}

          <div className="attribute-input">{this.renderInput()}</div>
          <style jsx>{`
            .attribute {
              user-select: none;
              transition: background 0.2s;
              font-size: 14px;
            }

            .attribute.is-flagged {
              border-left: 3px solid rgba(255, 6, 6);
              padding-left: 4px;
            }

            .attribute.flag-unconfirmed {
              background: rgba(247, 194, 189);
              border-radius: 0 4px 4px 0;
              padding-right: 4px;
            }

            .attribute + .attribute {
              margin-top: 12px;
            }

            .attribute.is-legend {
              background: rgba(200, 195, 222, 0.15);
            }

            .attribute.is-compact {
              display: grid;
              grid-template-columns: 100px auto;
              grid-column-gap: 12px;
              align-items: center;
            }
          `}</style>
        </div>
      </Tooltip>
    );
  }
}

export default AnnotationAttributeInput;

function getDotKeyValueLabel(uuid: string, attributes: AttributeSelections): string {
  const short = uuid.slice(uuid.length - 4);
  if (_.isString(attributes.key)) {
    return `(${short}) ${attributes.key}`;
  }
  if (_.isString(attributes.value)) {
    return `(${short}) ${attributes.value}`;
  }
  return `(${short})`;
}
